import * as THREE from 'three';

import { createNeutralLightingMaterial } from '../../../utils/glbUtils';
import { DXFEntity, DXFLayerConfig } from './types';

export function create3DMeshes(entities: DXFEntity[], layerConfig: Record<string, DXFLayerConfig>): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const layerGroups: Record<string, DXFEntity[]> = {};

  entities.forEach((e) => {
    const cfg = layerConfig[e.layer];
    if (!cfg || !cfg.enabled) return;
    if (!layerGroups[e.layer]) layerGroups[e.layer] = [];
    layerGroups[e.layer].push(e);
  });

  // Single shared material across every emitted mesh. These meshes are built
  // exclusively for `exportToGLB`; per-layer colour metadata is stored
  // elsewhere (server-side `meshes` payload), so the in-GLB material is
  // deliberately uniform and tuned for downstream lighting visualisation.
  const sharedMaterial = createNeutralLightingMaterial();

  Object.entries(layerGroups).forEach(([layerName, ents]) => {
    const cfg = layerConfig[layerName];
    const height = cfg.height || 1;

    ents.forEach((entity, ei) => {
      if (entity.vertices.length < 2) return;

      const shape = new THREE.Shape();
      shape.moveTo(entity.vertices[0][0], entity.vertices[0][1]);
      for (let i = 1; i < entity.vertices.length; i++) {
        shape.lineTo(entity.vertices[i][0], entity.vertices[i][1]);
      }

      if (entity.closed && entity.vertices.length >= 3) {
        shape.closePath();
        const geometry = new THREE.ExtrudeGeometry(shape, { depth: height, bevelEnabled: false });
        geometry.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(geometry, sharedMaterial);
        mesh.name = `${layerName}_${ei}`;
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        meshes.push(mesh);
      } else {
        // Open polyline -> thin wall
        const pts = entity.vertices.map((v) => new THREE.Vector2(v[0], v[1]));
        const wallWidth = 0.15;

        for (let i = 0; i < pts.length - 1; i++) {
          const a = pts[i];
          const b = pts[i + 1];
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len < 0.001) continue;
          const nx = ((-dy / len) * wallWidth) / 2;
          const ny = ((dx / len) * wallWidth) / 2;

          const wallShape = new THREE.Shape();
          wallShape.moveTo(a.x + nx, a.y + ny);
          wallShape.lineTo(b.x + nx, b.y + ny);
          wallShape.lineTo(b.x - nx, b.y - ny);
          wallShape.lineTo(a.x - nx, a.y - ny);
          wallShape.closePath();

          const geometry = new THREE.ExtrudeGeometry(wallShape, { depth: height, bevelEnabled: false });
          geometry.rotateX(-Math.PI / 2);
          const mesh = new THREE.Mesh(geometry, sharedMaterial);
          mesh.name = `${layerName}_wall_${ei}_${i}`;
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          meshes.push(mesh);
        }
      }
    });
  });

  return meshes;
}
